import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingContainer } from '@/components/shared/LoadingContainer';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { useToast } from '@/hooks/useToast';
import { AcquisitionProgress } from '../../components/AcquisitionProgress';
import { VacationBalanceCards } from '../../components/VacationBalanceCards';
import { VacationRequestForm } from '../../components/VacationRequestForm';
import type { VacationRequestFormResult } from '../../components/VacationRequestForm';
import { VacationRequestList } from '../../components/VacationRequestList';
import { buildReceiptsByRequestId } from '../../utils/vacationReceiptLinks';
import { findOpenVacationRequest } from '../../utils/vacationRequestStatus';
import { deriveDisplayBalance } from '../../utils/vacationBalanceDisplay';
import { buildFormBalance, resolveVacationAccess } from '../../utils/vacationEligibility';
import { resolveScheduleDeadline } from '../../utils/vacationScheduleDeadline';
import { OpenRequestPanel } from './components/OpenRequestPanel';
import { Notice } from './components/Notice';
import { FormLockedNotice } from './components/FormLockedNotice';
import { TourDemoForm } from './components/TourDemoForm';
import { TourDemoRequestCard } from './components/TourDemoRequestCard';
import {
    useMyVacationRequestsQuery,
    useVacationBalanceQuery,
    useVacationBalanceRestores,
    useHolidayDatesQuery,
    useVacationRequestMutations,
    useVacationReceiptsQuery,
} from '../../hooks/data';
import { useManagerSuggestions } from '../../hooks/data/useManagerSuggestions';
import { VacationHelp, VACATION_REQUEST_STEPS, REQUEST_RULES } from '../../components/VacationHelp';
import { PAGE_SUBTITLE, PAGE_TITLE, REQUESTS_LOADING_MESSAGE } from './vacationRequestPage.definitions';

export function VacationRequestPage() {
    const { user } = useAuth();
    const toast = useToast();
    const collaboratorId = user?.collaboratorId;
    const email = user?.email ?? undefined;

    const [shouldPollBalance, setShouldPollBalance] = useState(false);
    const [isTourDemo, setTourDemo] = useState(false);

    const { balance, isLoading: isBalanceLoading, error: balanceError } = useVacationBalanceQuery(
        collaboratorId,
        { poll: shouldPollBalance }
    );
    const balanceRestores = useVacationBalanceRestores(collaboratorId);
    const { holidayDates } = useHolidayDatesQuery();
    const {
        requests,
        isLoading: isRequestsLoading,
        error: requestsError,
    } = useMyVacationRequestsQuery(email);
    const { suggestions: managerSuggestions, hasMultipleAllocationManagers } = useManagerSuggestions(collaboratorId);
    const { receipts } = useVacationReceiptsQuery(collaboratorId, email);
    const { createRequest } = useVacationRequestMutations();

    const openRequest = useMemo(() => findOpenVacationRequest(requests), [requests]);

    const displayBalance = useMemo(
        () => deriveDisplayBalance(balance, requests, balanceRestores),
        [balance, requests, balanceRestores]
    );

    const receiptsByRequestId = useMemo(() => buildReceiptsByRequestId(receipts), [receipts]);

    const hasBalanceRestores = balanceRestores.length > 0;

    useEffect(() => {
        setShouldPollBalance(displayBalance.pendingReconciliation || hasBalanceRestores);
    }, [displayBalance.pendingReconciliation, hasBalanceRestores]);

    const access = useMemo(() => resolveVacationAccess(balance), [balance]);
    const scheduleDeadline = useMemo(
        () => resolveScheduleDeadline(balance, displayBalance.nextDeadline),
        [balance, displayBalance.nextDeadline]
    );
    const formBalance = useMemo(
        () => buildFormBalance(balance, displayBalance, access),
        [balance, displayBalance, access]
    );
    const isUpToDate = access.stage === 'entitled' && displayBalance.balanceDays <= 0;

    const handleSubmit = (result: VacationRequestFormResult) => {
        if (!access.canUseForm || isUpToDate) return;

        if (openRequest) {
            toast.error(
                'Solicitação em aberto',
                'Você já possui uma solicitação de férias aguardando aprovação ou em processamento.'
            );
            return;
        }

        if (!collaboratorId || !email) {
            toast.error(
                'Não foi possível enviar',
                'Seu cadastro de colaborador não foi encontrado. Procure o DP.'
            );
            return;
        }

        createRequest.mutate({
            collaboratorId,
            collaboratorEmail: email,
            modality: result.modality,
            segments: result.segments,
            sellDays: result.sellDays,
            managerEmail: result.managerEmail,
            clientEmails: result.clientEmails,
        });
    };

    const renderContent = () => {
        if (isBalanceLoading) {
            return <LoadingContainer message="Carregando seu saldo de férias..." />;
        }

        const isAcquiring = access.stage === 'tooEarly' || access.stage === 'earlyRequest';

        return (
            <div className="space-y-6">
                {!collaboratorId && (
                    <Notice>
                        Não encontramos seu cadastro de colaborador, então não é possível enviar
                        solicitações. Procure o DP para verificar seu registro.
                    </Notice>
                )}

                {balanceError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4" role="alert">
                        <p className="text-sm font-medium text-red-800 font-montserrat">
                            Não foi possível carregar seu saldo de férias
                        </p>
                        <p className="mt-1 text-sm text-red-700 font-montserrat">
                            {(balanceError as Error).message}
                        </p>
                    </div>
                )}

                <VacationBalanceCards
                    balanceDays={displayBalance.balanceDays}
                    expiredDays={displayBalance.expiredDays}
                    scheduledDays={displayBalance.scheduledDays}
                    deadline={scheduleDeadline.date}
                    deadlineVariant={scheduleDeadline.variant}
                    nextEntitlementDate={scheduleDeadline.nextEntitlementDate}
                />

                {isAcquiring && balance && (
                    <AcquisitionProgress
                        stage={access.stage as 'tooEarly' | 'earlyRequest'}
                        monthsWorked={balance.monthsWorked}
                        entitledFrom={balance.entitledFrom}
                        admissionDate={balance.admissionDate}
                        canRequestFrom={access.canRequestFrom}
                    />
                )}

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <section className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm" data-tour="new-vacation-button">
                        <h2 className="text-base font-semibold text-raro-gray-900 font-montserrat">
                            Nova solicitação
                        </h2>
                        {isTourDemo ? (
                            <div className="mt-4">
                                <TourDemoForm />
                            </div>
                        ) : isRequestsLoading ? (
                            <div className="mt-4">
                                <LoadingContainer message={REQUESTS_LOADING_MESSAGE} />
                            </div>
                        ) : openRequest ? (
                            <div className="mt-4">
                                <OpenRequestPanel request={openRequest} />
                            </div>
                        ) : !access.canUseForm ? (
                            <div className="mt-4">
                                <FormLockedNotice
                                    stage={access.stage as 'tooEarly' | 'ineligible' | 'unknown'}
                                    ineligibilityReason={balance?.ineligibilityReason}
                                />
                            </div>
                        ) : isUpToDate ? (
                            <div className="mt-4">
                                <FormLockedNotice
                                    stage="upToDate"
                                    nextEntitlementDate={scheduleDeadline.nextEntitlementDate}
                                />
                            </div>
                        ) : (
                            <VacationRequestForm
                                balance={formBalance}
                                earliestStartDate={access.earliestStartDate}
                                scheduleLimitDate={scheduleDeadline.variant === 'deadline' ? scheduleDeadline.date : null}
                                holidays={holidayDates}
                                isSubmitting={createRequest.isPending}
                                onSubmit={handleSubmit}
                                managerSuggestions={managerSuggestions}
                                hasMultipleAllocationManagers={hasMultipleAllocationManagers}
                                currentCollaboratorId={collaboratorId}
                            />
                        )}
                    </section>

                    <section className="rounded-xl border border-primary-200 bg-white p-6 shadow-sm" data-tour="vacation-requests-list">
                        <h2 className="mb-3 text-base font-semibold text-raro-gray-900 font-montserrat">
                            Minhas solicitações
                        </h2>
                        {isTourDemo ? (
                            <TourDemoRequestCard />
                        ) : (
                            <>
                                {requestsError && (
                                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4" role="alert">
                                        <p className="text-sm font-medium text-red-800 font-montserrat">
                                            Não foi possível carregar suas solicitações
                                        </p>
                                        <p className="mt-1 text-sm text-red-700 font-montserrat">
                                            {(requestsError as Error).message}
                                        </p>
                                    </div>
                                )}
                                <VacationRequestList requests={requests} isLoading={isRequestsLoading} receiptsByRequestId={receiptsByRequestId} />
                            </>
                        )}
                    </section>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <SectionHeader
                title={PAGE_TITLE}
                subtitle={PAGE_SUBTITLE}
                actions={
                    <VacationHelp
                        tourId="request"
                        steps={VACATION_REQUEST_STEPS}
                        rules={REQUEST_RULES}
                        autoStartTour={false}
                        ready={!isBalanceLoading}
                        onTourStart={() => setTourDemo(true)}
                        onTourEnd={() => setTourDemo(false)}
                    />
                }
            />

            {renderContent()}
        </div>
    );
}

export default VacationRequestPage;
